/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { TFunction } from 'i18next';
import { DisclaimerUrls, buildDisclaimer } from '../../utils/signupLoginUtils';
import { defaultProps, LoginPageProps } from './types';

export type { DisclaimerUrls };

export const buildLoginViewProps = (
  props: LoginPageProps,
  t: TFunction,
  urls: DisclaimerUrls = {},
) => {
  const headings = {
    ...(defaultProps.headings ?? {}),
    ...props.headings,
    greeting: {
      ...(defaultProps.headings?.greeting ?? {}),
      text: t('loginPage.greeting'),
      ...props.headings?.greeting,
    },
    prompt: {
      ...(defaultProps.headings?.prompt ?? {}),
      text: t('loginPage.noAccount'),
      ...props.headings?.prompt,
    },
    action: {
      ...(defaultProps.headings?.action ?? {}),
      text: t('loginPage.signUp'),
      ...props.headings?.action,
    },
    forgotPassword: {
      ...(defaultProps.headings?.forgotPassword ?? {}),
      text: t('loginPage.forgotPassword'),
      ...props.headings?.forgotPassword,
    },
    dividerText: {
      ...(defaultProps.headings?.dividerText ?? {}),
      text: t('loginPage.dividerText'),
      ...props.headings?.dividerText,
    },
    continueAsGuestProps: {
      ...(defaultProps.headings?.continueAsGuestProps ?? {}),
      text: t('loginPage.continueAsGuest'),
      ...props.headings?.continueAsGuestProps,
    },
  };

  const emailFieldProps = {
    ...defaultProps.emailFieldProps,
    label: t('loginPage.emailLabel'),
    ...props.emailFieldProps,
  };

  const passwordFieldProps = {
    ...defaultProps.passwordFieldProps,
    label: t('loginPage.passwordLabel'),
    ...props.passwordFieldProps,
  };

  const mobileFieldProps = {
    ...defaultProps.mobileFieldProps,
    label: t('loginPage.mobileNumberLabel'),
    ...props.mobileFieldProps,
  };

  const dialingCodeFieldProps = {
    ...defaultProps.dialingCodeFieldProps,
    label: t('loginPage.dialCodeLabel'),
    ...props.dialingCodeFieldProps,
  };

  const primaryButtonProps = {
    ...defaultProps.primaryButtonProps,
    text: t('loginPage.loginButton'),
    ...props.primaryButtonProps,
  };

  const loginWithMobileNumber =
    props.loginWithMobileNumber ?? defaultProps.loginWithMobileNumber;

  const loginWithPasskey = {
    ...defaultProps.loginWithPasskey,
    text: t('loginPage.loginWithPasskey'),
    ...props.loginWithPasskey,
  };

  const socialLogins = {
    ...(defaultProps.socialLogins ?? {}),
    ...props.socialLogins,
    facebook: {
      ...(defaultProps.socialLogins?.facebook ?? {}),
      ...props.socialLogins?.facebook,
      label: {
        ...(defaultProps.socialLogins?.facebook?.label ?? {}),
        text: 'Facebook',
        ...props.socialLogins?.facebook?.label,
      },
    },
    google: {
      ...(defaultProps.socialLogins?.google ?? {}),
      ...props.socialLogins?.google,
      label: {
        ...(defaultProps.socialLogins?.google?.label ?? {}),
        text: 'Google',
        ...props.socialLogins?.google?.label,
      },
    },
    apple: {
      ...(defaultProps.socialLogins?.apple ?? {}),
      ...props.socialLogins?.apple,
      label: {
        ...(defaultProps.socialLogins?.apple?.label ?? {}),
        text: 'Apple',
        ...props.socialLogins?.apple?.label,
      },
    },
    wechat: {
      ...(defaultProps.socialLogins?.wechat ?? {}),
      ...props.socialLogins?.wechat,
      label: {
        ...(defaultProps.socialLogins?.wechat?.label ?? {}),
        text: 'WeChat',
        ...props.socialLogins?.wechat?.label,
      },
    },
  };

  const disclaimer = buildDisclaimer(
    {
      ...(props.disclaimer ?? defaultProps.disclaimer ?? { text: '' }),
      text: t('loginView.disclaimer'),
    },
    t,
    urls,
  );

  return {
    headings,
    emailFieldProps,
    passwordFieldProps,
    mobileFieldProps,
    dialingCodeFieldProps,
    primaryButtonProps,
    loginWithMobileNumber,
    loginWithPasskey,
    socialLogins,
    disclaimer,
  };
};

export type LoginViewResolvedProps = ReturnType<typeof buildLoginViewProps>;
